import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Settings as SettingsIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type CtSettings = {
  id: string;
  enabled: boolean;
  defaultBatchSize: number;
  prefetchThreshold: number;
  maxConcurrentRenders: number;
  endpoints: Record<string, string>;
  defaults: { aspectRatio: "1:1" | "4:5" | "9:16"; mockMode: boolean };
};

export default function CreativeTinderSettingsPage() {
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery<CtSettings>({ queryKey: ["/api/creative-tinder/settings"] });
  const [form, setForm] = useState<CtSettings | null>(null);
  useEffect(() => { if (data && !form) setForm(data); }, [data, form]);

  if (error) return <div className="p-8 text-sm text-red-600">Failed to load settings: {(error as Error).message}</div>;

  const save = useMutation({
    mutationFn: async (next: CtSettings) => {
      const r = await apiRequest("PUT", "/api/creative-tinder/settings", next);
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/creative-tinder/settings"] });
      toast({ title: "Saved", description: "Creative Tinder settings updated." });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e?.message ?? "Unknown error", variant: "destructive" }),
  });

  if (isLoading || !form) return <div className="p-8"><Loader2 className="animate-spin h-5 w-5" /></div>;

  const setEp = (k: string, v: string) => setForm({ ...form, endpoints: { ...form.endpoints, [k]: v } });

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <SettingsIcon className="h-6 w-6 text-violet-500" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Creative Tinder Settings</h1>
          <p className="text-sm text-muted-foreground">Endpoints, runtime, and the tool's on/off flag.</p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Tool</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Enable Creative Tinder</Label>
              <p className="text-xs text-muted-foreground">When off, the menu item and page are hidden.</p>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
              data-testid="switch-enabled"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Endpoints</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {[
            { k: "conceptBatchEndpoint", label: "Concept Batch Endpoint" },
            { k: "promptMakerEndpoint", label: "Prompt Maker Endpoint" },
            { k: "imageRenderEndpoint", label: "Image Render Endpoint" },
            { k: "imageQaEndpoint", label: "Image QA Endpoint (optional)" },
            { k: "textOverlayEndpoint", label: "Text Overlay Endpoint (optional)" },
            { k: "futureStoryboardEndpoint", label: "Future: Storyboard (placeholder)" },
            { k: "futureSetExpansionEndpoint", label: "Future: Set Expansion (placeholder)" },
          ].map(({ k, label }) => (
            <div key={k} className="space-y-1">
              <Label className="text-xs">{label}</Label>
              <Input
                value={form.endpoints[k] ?? ""}
                onChange={(e) => setEp(k, e.target.value)}
                placeholder="Leave blank to use mock mode"
                data-testid={`input-endpoint-${k}`}
              />
            </div>
          ))}
          <p className="text-xs text-muted-foreground">Blank endpoints fall back to mock mode automatically.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Runtime</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Default batch size</Label>
              <Input type="number" value={form.defaultBatchSize} onChange={(e) => setForm({ ...form, defaultBatchSize: parseInt(e.target.value || "30", 10) })} data-testid="input-batch-size" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Prefetch threshold</Label>
              <Input type="number" value={form.prefetchThreshold} onChange={(e) => setForm({ ...form, prefetchThreshold: parseInt(e.target.value || "3", 10) })} data-testid="input-prefetch" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Max concurrent renders</Label>
              <Input type="number" value={form.maxConcurrentRenders} onChange={(e) => setForm({ ...form, maxConcurrentRenders: parseInt(e.target.value || "3", 10) })} data-testid="input-concurrent" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Default aspect ratio</Label>
              <Select value={form.defaults?.aspectRatio || "1:1"} onValueChange={(v) => setForm({ ...form, defaults: { ...form.defaults, aspectRatio: v as any } })}>
                <SelectTrigger data-testid="select-aspect"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1:1">1:1</SelectItem>
                  <SelectItem value="4:5">4:5</SelectItem>
                  <SelectItem value="9:16">9:16</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <Label className="text-xs">Force mock mode</Label>
              <Switch
                checked={!!form.defaults?.mockMode}
                onCheckedChange={(v) => setForm({ ...form, defaults: { ...form.defaults, mockMode: v } })}
                data-testid="switch-mock"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => save.mutate(form)} disabled={save.isPending} data-testid="button-save-settings">
          {save.isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving</> : <><Save className="h-4 w-4 mr-1.5" /> Save</>}
        </Button>
      </div>
    </div>
  );
}
